"use client"

import { MediaGalleryAdSlot } from "@/components/ads/MediaGalleryAdSlot"
import { type BooruPost } from "@/lib/booru-api"
import { AlertCircle } from "@/lib/icons"
import { cleanPlayerUrl, getVideoCrossOrigin, isMediaWorkerUrl, mediaWorkerFallbackUrl } from "@/lib/image-utils"
import { useVRStore } from "@/lib/vr-store"
import { useTranslations } from "next-intl"
import Image from "next/image"
import type React from "react"
import { useState } from "react"

const AD_NOTE_KEY = "javapi-ad-note-dismissed"

/** Whether an <img>'s src (always absolute) is already this address (which may be relative, like a proxied one). */
const isSameAddress = (src: string, url: string) => {
  try {
    return new URL(src, window.location.href).href === new URL(url, window.location.href).href
  } catch {
    return src === url
  }
}

/** Most addresses one failing viewer image is switched to before it gives up and shows the placeholder. */
const MAX_IMAGE_RETRIES = 4

/** Counts a retry on this <img>; false once it has used them all (a safety net so a failing image can never loop). */
const takeRetry = (img: HTMLImageElement) => {
  const used = Number(img.dataset.retries || "0")
  if (used >= MAX_IMAGE_RETRIES) return false
  img.dataset.retries = String(used + 1)
  return true
}

// Extracted from components/booru-detail-modal.tsx (pure move: same JSX, same behaviour).
interface MediaDisplayAreaProps {
  activeVideoUrl: string | null
  allowVideoZoom: boolean
  autoplayVideos: boolean
  currentAlbumImageUrl: string | null
  currentAlbumPage: number
  enableMousePan: boolean
  getCurrentVideoUrl: () => string
  getMediaUrl: (url?: string | null, isFullSize?: boolean) => string
  getPreferredMediaUrl: () => string
  hasGifTag: any
  hideUI: boolean
  imageDisplayMode: "contain" | "cover"
  isAlbum: boolean
  isCurrentAlbumPageVideo: boolean
  isLoadingFullImage: boolean
  isLooping: boolean
  isMediaVideo: boolean
  isMuted: boolean
  isPanning: boolean
  isVideoLoading: boolean
  lazyLoadedEmbedUrl: string | null
  lazyLoadedVideoUrl: string | null
  panX: number
  panY: number
  post: BooruPost
  setContainerAR: React.Dispatch<React.SetStateAction<number | null>>
  setContainerH: React.Dispatch<React.SetStateAction<number | null>>
  setCurrentAlbumPage: React.Dispatch<React.SetStateAction<number>>
  setCurrentTime: (t: number) => void
  setDuration: React.Dispatch<React.SetStateAction<number>>
  setHideUI: React.Dispatch<React.SetStateAction<boolean>>
  setIsVideoLoading: React.Dispatch<React.SetStateAction<boolean>>
  setMediaNativeAR: React.Dispatch<React.SetStateAction<number | null>>
  setShouldFallbackToImage: React.Dispatch<React.SetStateAction<boolean>>
  setTriedExtensions: React.Dispatch<React.SetStateAction<Set<string>>>
  setVideoError: React.Dispatch<React.SetStateAction<string | null>>
  setVideoExtensionIndex: React.Dispatch<React.SetStateAction<number>>
  setViewingAdSlot: React.Dispatch<React.SetStateAction<number | null>>
  shouldFallbackToImage: boolean
  totalAlbumPages: number
  triedExtensions: Set<string>
  videoError: string | null
  videoExtensionIndex: number
  videoExtensions: string[]
  videoPitchCorrection: boolean
  videoRef: React.RefObject<HTMLVideoElement | null>
  viewingAdSlot: number | null
  zoom: number
}

export function MediaDisplayArea({
  activeVideoUrl,
  allowVideoZoom,
  autoplayVideos,
  currentAlbumImageUrl,
  currentAlbumPage,
  enableMousePan,
  getCurrentVideoUrl,
  getMediaUrl,
  getPreferredMediaUrl,
  hasGifTag,
  hideUI,
  imageDisplayMode,
  isAlbum,
  isCurrentAlbumPageVideo,
  isLoadingFullImage,
  isLooping,
  isMediaVideo,
  isMuted,
  isPanning,
  isVideoLoading,
  lazyLoadedEmbedUrl,
  lazyLoadedVideoUrl,
  panX,
  panY,
  post,
  setContainerAR,
  setContainerH,
  setCurrentAlbumPage,
  setCurrentTime,
  setDuration,
  setHideUI,
  setIsVideoLoading,
  setMediaNativeAR,
  setShouldFallbackToImage,
  setTriedExtensions,
  setVideoError,
  setVideoExtensionIndex,
  setViewingAdSlot,
  shouldFallbackToImage,
  totalAlbumPages,
  triedExtensions,
  videoError,
  videoExtensionIndex,
  videoExtensions,
  videoPitchCorrection,
  videoRef,
  viewingAdSlot,
  zoom,
}: MediaDisplayAreaProps) {
  const tt = useTranslations("booruDetailModal")
  // JAVAPI and Goontai players without their ads, served by the media Worker (null when the Worker is not set up); see the iframe below.
  // The note under the original JAVAPI player (ads inside it are not ours); closed for good once dismissed.
  const [adNoteDismissed, setAdNoteDismissed] = useState(() => {
    try {
      // The X hides it for this visit (sessionStorage); "Don't show again" hides it for good (localStorage).
      return typeof window !== "undefined" && (localStorage.getItem(AD_NOTE_KEY) === "1" || sessionStorage.getItem(AD_NOTE_KEY) === "1")
    } catch {
      return false
    }
  })
  const cleanPlayer = cleanPlayerUrl(post?.site, post?.rawPost?.postId ?? post?.rawPost?.slug)
  return (
    <>
      {viewingAdSlot !== null ? (
                  <MediaGalleryAdSlot
                    slotIndex={viewingAdSlot}
                    variant="full"
                    className="absolute inset-0"
                    onDismiss={() => {
                      // Dismissing (the X button) skips straight to the real
                      // page this ad was inserted in front of.
                      setViewingAdSlot(null)
                      setCurrentAlbumPage((prev) => Math.min(prev + 1, totalAlbumPages - 1))
                    }}
                  />
                ) : isMediaVideo && (activeVideoUrl || post.rawPost?.embedUrl) && !shouldFallbackToImage ? (
                  <>
                    <div
                      className="w-full h-full flex items-center justify-center"
                      style={{
                        transform: `scale(${allowVideoZoom ? zoom : 1}) translate(${(allowVideoZoom ? panX : 0) / (allowVideoZoom ? zoom : 1)}px, ${(allowVideoZoom ? panY : 0) / (allowVideoZoom ? zoom : 1)}px)`,
                        cursor: isPanning ? "grabbing" : zoom > 1 && enableMousePan && allowVideoZoom ? "grab" : "default",
                      }}
                      onClick={() => setHideUI(!hideUI)}
                    >
                      {/* For XlecX videos, wait for lazy load to complete before rendering video */}
                      {post.site === 'xlecx' && post.type === 'video' && !lazyLoadedVideoUrl && isLoadingFullImage ? (
                        <div className="flex items-center justify-center w-full h-full">
                          <div className="text-center">
                            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
                            <p className="text-sm text-muted-foreground">{tt("loadingVideo")}</p>
                          </div>
                        </div>
                      ) : (post.site === 'goontai' || post.site === 'javapi' || post.site === 'javapi_hentai') && (lazyLoadedEmbedUrl || post.rawPost?.embedUrl || post.rawPost?.isJavapi) ? (
                        <div className="w-full h-full flex items-center justify-center bg-black">
                          <div className="w-full" style={{ maxHeight: '100%', aspectRatio: '16/9' }}>
                            {/* For JAVAPI without the cleaned player, wait for the embed URL from the player API before rendering */}
                            {(post.site === 'javapi' || post.site === 'javapi_hentai') && !cleanPlayer && !lazyLoadedEmbedUrl ? (
                              <div className="w-full h-full flex items-center justify-center">
                                <div className="text-center">
                                  <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary mx-auto mb-3" />
                                  <p className="text-sm text-muted-foreground">{tt("loadingPlayer")}</p>
                                </div>
                              </div>
                            ) : (
                              <>
                                {/* JAVAPI's own player page carries scam ads (fake virus warnings, fake "I'm not a robot", pop-under tabs on the
                                    first clicks). With the media Worker it plays from a cleaned copy of that page (lib/player-embed.ts). Without the
                                    Worker the original page is shown as it always was and a note says the ads are not ours. It must NOT be sandboxed:
                                    the player detects any sandbox and refuses to play ("Playback Restricted"). */}
                                {/* The original player's ads are not ours: say so, and what to do with them. */}
                                {/* Only the JAV server's player has ads; the hentai server's page has none, so no note there. */}
                                {post.site === 'javapi' && !cleanPlayer && !adNoteDismissed && (
                                  <div className="relative z-20">
                                  <div role="note" className="absolute inset-x-0 bottom-full mb-2 flex flex-wrap items-center gap-2 rounded-md border border-red-500 bg-black/90 px-3 py-2 text-xs font-medium leading-snug text-red-100 shadow-lg">
                                    <span aria-hidden className="shrink-0 text-red-400">⚠</span>
                                    <span className="flex-1">{tt("playerAdsNotOurs")}</span>
                                    <button
                                      type="button"
                                      onClick={() => {
                                        setAdNoteDismissed(true)
                                        try {
                                          localStorage.setItem(AD_NOTE_KEY, "1")
                                        } catch {
                                          // the note simply comes back next time
                                        }
                                      }}
                                      className="shrink-0 rounded border border-red-500/60 px-2 py-0.5 text-[11px] text-red-100 hover:bg-red-500/20"
                                    >
                                      {tt("dontShowAgain")}
                                    </button>
                                    <button
                                      type="button"
                                      aria-label={tt("dismissNote")}
                                      onClick={() => {
                                        setAdNoteDismissed(true)
                                        try {
                                          sessionStorage.setItem(AD_NOTE_KEY, "1")
                                        } catch {
                                          // the note simply comes back next time
                                        }
                                      }}
                                      className="shrink-0 rounded px-1.5 text-sm text-red-200 hover:bg-red-500/20 hover:text-white"
                                    >
                                      ✕
                                    </button>
                                  </div>
                                  </div>
                                )}
                                <iframe
                                  src={cleanPlayer ?? (lazyLoadedEmbedUrl || post.rawPost?.embedUrl)?.replace(/&#038;/g, '&').replace(/&amp;/g, '&')}
                                  className="w-full h-full border-0"
                                  allowFullScreen
                                  allow="autoplay; fullscreen; encrypted-media; picture-in-picture"
                                  referrerPolicy={cleanPlayer ? "origin" : undefined}
                                  {...(post.site === 'goontai' ? { sandbox: "allow-scripts allow-same-origin allow-forms allow-presentation" } : {})}
                                  title={post.title ?? tt("video")}
                                />
                                {/* Fallback link for PRO/restricted videos */}
                                {(post.site === 'javapi' || post.site === 'javapi_hentai') && (
                                  <a
                                    href={`${post.rawPost?.server?.replace('/wp-json/myvideo/v1', '')}/?p=${post.id}`}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="absolute bottom-2 right-2 text-xs bg-black/70 text-white/70 hover:text-white px-2 py-1 rounded transition-colors"
                                  >
                                    {tt("watchOnAPIJAV")}
                                  </a>
                                )}
                              </>
                            )}
                          </div>
                        </div>
                      ) : (
                      <video
                        key={`video-${post.site}-${post.id}-${lazyLoadedVideoUrl || activeVideoUrl || post.fileUrl}`}
                        ref={videoRef}
                        src={getCurrentVideoUrl()}
                        crossOrigin={getVideoCrossOrigin(getCurrentVideoUrl())}
                        poster={post.previewUrl ? getMediaUrl(post.previewUrl) : undefined}
                        preload="metadata"
                        className="w-full h-full object-contain select-none"
                        autoPlay={autoplayVideos}
                        loop={hasGifTag || isLooping}
                        onLoadStart={() => {
                          setIsVideoLoading(true)
                          setVideoError(null)
                        }}
                        onLoadedData={() => {
                          setIsVideoLoading(false)
                        }}
                        onLoadedMetadata={(e) => {
                          const videoDuration = e.currentTarget.duration
                          // Validate duration is a finite number
                          if (Number.isFinite(videoDuration) && videoDuration > 0) {
                            setDuration(videoDuration)
                          } else {
                            setDuration(0)
                          }
                          e.currentTarget.preservesPitch = videoPitchCorrection
                          // Capture native video aspect ratio for letterbox ad
                          const vw = e.currentTarget.videoWidth
                          const vh = e.currentTarget.videoHeight
                          if (vw > 0 && vh > 0) setMediaNativeAR(vw / vh)
                          // Capture container aspect ratio
                          const container = e.currentTarget.closest('[data-media-container]') as HTMLElement | null
                          if (container) {
                            setContainerAR(container.offsetWidth / container.offsetHeight)
                            setContainerH(container.offsetHeight)
                          }
                          // Several sources (e.g. Civitai) have no real static
                          // thumbnail for video posts — previewUrl points at the
                          // video file itself, which a browser can't decode as a
                          // poster image, so `poster` silently fails. Without
                          // autoplay, a freshly loaded paused video then just
                          // shows a black square instead of its first frame.
                          // Nudging currentTime forces the browser to decode and
                          // paint a real frame regardless of whether poster works.
                          if (!autoplayVideos && e.currentTarget.currentTime === 0) {
                            e.currentTarget.currentTime = 0.1
                          }
                        }}
                        onTimeUpdate={(e) => {
                          const time = e.currentTarget.currentTime
                          // Validate time is a finite number; skipped while the VR player
                          // covers this modal (see the rAF playhead loop for why).
                          if (Number.isFinite(time) && !useVRStore.getState().isVRMode) {
                            setCurrentTime(time)
                          }
                        }}
                        onError={(e) => {
                          // A video served by the Cloudflare media Worker that fails is retried once through the Vercel proxy.
                          const workerFallback = mediaWorkerFallbackUrl(e.currentTarget.currentSrc || e.currentTarget.src)
                          if (workerFallback && e.currentTarget.dataset.workerRetried !== "1") {
                            e.currentTarget.dataset.workerRetried = "1"
                            setIsVideoLoading(true)
                            e.currentTarget.src = workerFallback
                            e.currentTarget.load()
                            return
                          }
                          setIsVideoLoading(false)
                          const error = e.currentTarget.error
                          let errorMsg = "Video failed to load"
                          let shouldFallback = false
                          let shouldTryNextExtension = false
                          
                          if (error) {
                            switch (error.code) {
                              case MediaError.MEDIA_ERR_ABORTED:
                                errorMsg = "Video loading was aborted"
                                break
                              case MediaError.MEDIA_ERR_NETWORK:
                                errorMsg = "Network error - video may be deleted or unavailable (404)"
                                // Only try extension cycling for XlecX
                                shouldTryNextExtension = post.site === 'xlecx'
                                break
                              case MediaError.MEDIA_ERR_DECODE:
                                errorMsg = "Video format not supported or file is corrupted"
                                shouldTryNextExtension = post.site === 'xlecx'
                                break
                              case MediaError.MEDIA_ERR_SRC_NOT_SUPPORTED:
                                errorMsg = "Video format not supported by your browser"
                                shouldTryNextExtension = post.site === 'xlecx'
                                break
                              default:
                                errorMsg = error.message || "Unknown video error"
                                shouldTryNextExtension = post.site === 'xlecx'
                            }
                          }
                          
                          // Check if this is a GIF that should fallback to image display
                          const isGifFile = post.fileUrl?.toLowerCase().includes('.gif')
                          
                          // Try cycling through extensions before giving up (ONLY for XlecX)
                          if (shouldTryNextExtension && videoExtensionIndex < videoExtensions.length - 1) {
                            const currentUrl = getCurrentVideoUrl()
                            const currentExt = videoExtensions[videoExtensionIndex]
                            
                            // Mark this extension as tried
                            setTriedExtensions(prev => new Set([...prev, currentExt]))
                            
                            console.log(`[v0] 🔄 XlecX video failed with ${currentExt}, trying next extension...`)
                            console.log(`[v0] Failed URL: ${currentUrl}`)
                            console.log(`[v0] Tried extensions so far: ${Array.from(triedExtensions).join(', ')}`)
                            
                            // Try next extension
                            setVideoExtensionIndex(prev => prev + 1)
                            setVideoError(null) // Clear error while trying next extension
                            return
                          }
                          
                          // All extensions tried or should fallback
                          if (shouldFallback || isGifFile) {
                            console.error("[v0] Video decode/format error, falling back to image:", errorMsg, getCurrentVideoUrl() || activeVideoUrl)
                            if (post.site === 'xlecx') {
                              console.log(`[v0] Tried all extensions: ${Array.from(triedExtensions).join(', ')}`)
                            }
                            setShouldFallbackToImage(true)
                          } else {
                            console.error("[v0] Video error:", errorMsg, getCurrentVideoUrl() || activeVideoUrl)
                            if (post.site === 'xlecx' && triedExtensions.size > 0) {
                              console.log(`[v0] Tried extensions: ${Array.from(triedExtensions).join(', ')}`)
                            }
                            setVideoError(errorMsg)
                            // The browser's MediaError API never exposes the real HTTP status — a
                            // removed/410'd source file looks identical to "format not supported,"
                            // which makes users think Goonru itself is broken. Check the actual
                            // status server-side and replace the message when the source removed it.
                            // Must check the actual requested src (getCurrentVideoUrl()) — for
                            // xlecx/rule34 that's a lazily-resolved URL, not post.fileUrl.
                            const checkUrl = getCurrentVideoUrl() || activeVideoUrl || post.fileUrl
                            if (checkUrl) {
                              fetch(`/api/proxy/check-media-status?url=${encodeURIComponent(checkUrl)}`)
                                .then(r => r.json())
                                .then(data => {
                                  if (data.removed) {
                                    setVideoError("This content was removed from the source site and is no longer available.")
                                  }
                                })
                                .catch(() => {})
                            }
                          }
                        }}
                        muted={isMuted}
                        playsInline
                      />
                      )}
                    </div>
                    {/* Show GIF label for content with gif tags */}
                    {hasGifTag && (
                      <div className="absolute top-4 right-4 bg-black/70 text-white text-xs px-2 py-1 rounded font-semibold z-10">
                        GIF
                      </div>
                    )}
                    {isVideoLoading && (
                      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                        <div className="h-10 w-10 animate-spin rounded-full border-4 border-solid border-white/80 border-r-transparent" />
                      </div>
                    )}
                    {videoError && (
                      <div className="absolute inset-0 flex items-center justify-center pointer-events-none bg-black/50">
                        <div className="bg-red-500/90 text-white px-6 py-4 rounded-lg max-w-md text-center">
                          <AlertCircle className="h-8 w-8 mx-auto mb-2" />
                          <p className="font-semibold mb-1">{tt("videoUnavailable")}</p>
                          <p className="text-sm opacity-90">{videoError}</p>
                          {post.site === "4chan" && (
                            <p className="text-xs opacity-75 mt-2">
                              {tt("note4chanVideosHaveNoSoundSitePolicy")}
                            </p>
                          )}
                        </div>
                      </div>
                    )}
                  </>
                ) : isAlbum && currentAlbumImageUrl ? (
                  isCurrentAlbumPageVideo ? (
                    <>
                      <div
                        className="w-full h-full flex items-center justify-center"
                        style={{
                          transform: `scale(${allowVideoZoom ? zoom : 1}) translate(${(allowVideoZoom ? panX : 0) / (allowVideoZoom ? zoom : 1)}px, ${(allowVideoZoom ? panY : 0) / (allowVideoZoom ? zoom : 1)}px)`,
                          cursor:
                            isPanning ? "grabbing" : zoom > 1 && enableMousePan && allowVideoZoom ? "grab" : "default",
                        }}
                        onClick={() => setHideUI(!hideUI)}
                      >
                        <video
                          key={currentAlbumImageUrl}
                          ref={videoRef}
                          src={getMediaUrl(currentAlbumImageUrl)}
                          // Album pages have no per-page thumbnail of their own
                          // (albumPages is just the raw media URLs), so this
                          // falls back to the album cover — still far better
                          // than the pure black frame this had before, which
                          // read identically to "broken" rather than "paused".
                          poster={post.previewUrl ? getMediaUrl(post.previewUrl) : undefined}
                          preload="metadata"
                          className="w-full h-full object-contain select-none"
                          autoPlay={autoplayVideos}
                          loop={hasGifTag || isLooping}
                          muted={isMuted}
                          playsInline
                          onLoadStart={() => {
                            setIsVideoLoading(true)
                            setVideoError(null)
                          }}
                          onLoadedData={() => setIsVideoLoading(false)}
                          onLoadedMetadata={(e) => {
                            const videoDuration = e.currentTarget.duration
                            // Validate duration is a finite number
                            if (Number.isFinite(videoDuration) && videoDuration > 0) {
                              setDuration(videoDuration)
                            } else {
                              setDuration(0)
                            }
                            e.currentTarget.preservesPitch = videoPitchCorrection
                            // See the non-album video block above — poster
                            // often can't work for these sources, so nudge
                            // currentTime to force a real first frame to paint
                            // instead of a black square while paused.
                            if (!autoplayVideos && e.currentTarget.currentTime === 0) {
                              e.currentTarget.currentTime = 0.1
                            }
                          }}
                          onTimeUpdate={(e) => {
                            const time = e.currentTarget.currentTime
                            // Validate time is a finite number; skipped while the VR
                            // player covers this modal.
                            if (Number.isFinite(time) && !useVRStore.getState().isVRMode) {
                              setCurrentTime(time)
                            }
                          }}
                          onError={(e) => {
                            setIsVideoLoading(false)
                            const error = e.currentTarget.error
                            let errorMsg = "Video failed to load"
                            
                            if (error) {
                              switch (error.code) {
                                case MediaError.MEDIA_ERR_ABORTED:
                                  errorMsg = "Video loading was aborted"
                                  break
                                case MediaError.MEDIA_ERR_NETWORK:
                                  errorMsg = "Network error - video may be deleted or unavailable"
                                  break
                                case MediaError.MEDIA_ERR_DECODE:
                                  errorMsg = "Video format not supported or file is corrupted"
                                  break
                                case MediaError.MEDIA_ERR_SRC_NOT_SUPPORTED:
                                  errorMsg = "Video format not supported by your browser"
                                  break
                                default:
                                  errorMsg = error.message || "Unknown video error"
                              }
                            }
                            
                            console.error("[v0] Album video error:", errorMsg, currentAlbumImageUrl)
                            setVideoError(errorMsg)
                            // See the main video onError handler above for why this check exists —
                            // a removed/410'd source file reports as a generic "format not
                            // supported" error, which makes users think Goonru itself is broken.
                            if (currentAlbumImageUrl) {
                              fetch(`/api/proxy/check-media-status?url=${encodeURIComponent(currentAlbumImageUrl)}`)
                                .then(r => r.json())
                                .then(data => {
                                  if (data.removed) {
                                    setVideoError("This content was removed from the source site and is no longer available.")
                                  }
                                })
                                .catch((err) => console.error("[v0] check-media-status failed:", err))
                            }
                          }}
                          controls={false}
                        />
                      </div>
                      {isVideoLoading && (
                        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                          <div className="h-10 w-10 animate-spin rounded-full border-4 border-solid border-white/80 border-r-transparent" />
                        </div>
                      )}
                      {videoError && (
                        <div className="absolute inset-0 flex items-center justify-center pointer-events-none bg-black/50">
                          <div className="bg-red-500/90 text-white px-6 py-4 rounded-lg max-w-md text-center">
                            <AlertCircle className="h-8 w-8 mx-auto mb-2" />
                            <p className="font-semibold mb-1">{tt("videoUnavailable")}</p>
                            <p className="text-sm opacity-90">{videoError}</p>
                            {post.site === "4chan" && (
                              <p className="text-xs opacity-75 mt-2">
                                {tt("note4chanVideosHaveNoSoundSitePolicy")}
                              </p>
                            )}
                          </div>
                        </div>
                      )}
                    </>
                  ) : (
                    <Image
                      src={getMediaUrl(currentAlbumImageUrl) || "/placeholder.svg"}
                      alt={`Post ${post.id} - Page ${currentAlbumPage + 1}`}
                      width={(post.width && post.width > 0) ? post.width : 1200}
                      height={(post.height && post.height > 0) ? post.height : 800}
                      unoptimized
                      className="max-w-full max-h-full w-auto h-auto object-contain select-none"
                      style={{
                        transform: `scale(${zoom}) translate(${panX / zoom}px, ${panY / zoom}px)`,
                        cursor: isPanning ? "grabbing" : zoom > 1 && enableMousePan ? "grab" : "default",
                        willChange: zoom > 1 || isPanning ? "transform" : "auto",
                      }}
                      onClick={(e) => {
                        if (e.target === e.currentTarget) {
                          setHideUI(!hideUI)
                        }
                      }}
                      onLoad={(e) => {
                        e.currentTarget.dataset.retries = "0"
                      }}
                      onError={(e) => {
                        // A page from the media Worker gets one retry through the Vercel proxy first (a Worker limit or outage).
                        const workerFallback = mediaWorkerFallbackUrl(e.currentTarget.src)
                        if (workerFallback) {
                          e.currentTarget.src = workerFallback
                          return
                        }
                        console.log("[v0] Main image failed to load. Trying preview URL...")
                        if (post.previewUrl && !isSameAddress(e.currentTarget.src, getMediaUrl(post.previewUrl)) && takeRetry(e.currentTarget)) {
                          e.currentTarget.src = getMediaUrl(post.previewUrl!)
                        } else {
                          e.currentTarget.src = "/placeholder.svg?height=400&width=400"
                        }
                      }}
                      // The media Worker only serves our own site, which it knows by the referrer (origin only, no page path).
                      referrerPolicy={isMediaWorkerUrl(getMediaUrl(currentAlbumImageUrl)) ? "origin" : "no-referrer"}
                      priority
                      draggable={false}
                    />
                  )
                ) : post.fileUrl || post.sampleUrl ? (
                  <>
                    {/* Debug logging for URL issues */}
                    {(() => {
                      const isGifFile = post.fileUrl?.toLowerCase().includes('.gif') || post.sampleUrl?.toLowerCase().includes('.gif')
                      console.log('[v0] BooruDetailModal image URLs:', {
                        site: post.site,
                        id: post.id,
                        shouldFallbackToImage,
                        isGifFile,
                        fileUrl: post.fileUrl,
                        sampleUrl: post.sampleUrl,
                        previewUrl: post.previewUrl,
                        selectedUrl: shouldFallbackToImage 
                          ? (post.sampleUrl || post.previewUrl || post.fileUrl || "")
                          : getPreferredMediaUrl()
                      })
                      return null
                    })()}
                    <Image
                      src={shouldFallbackToImage 
                        ? getMediaUrl(post.sampleUrl || post.previewUrl || post.fileUrl || "", false)
                        : getPreferredMediaUrl()
                      }
                      alt={`Post ${post.id}`}
                      width={(post.width && post.width > 0) ? post.width : 1200}
                      height={(post.height && post.height > 0) ? post.height : 800}
                      unoptimized
                      className={`max-w-full max-h-full w-auto h-auto select-none ${imageDisplayMode === 'contain' ? 'object-contain' : 'object-cover'}`}
                      style={{
                        transform: `scale(${zoom}) translate(${panX / zoom}px, ${panY / zoom}px)`,
                        cursor: isPanning ? "grabbing" : (zoom > 1 || imageDisplayMode === 'cover') && enableMousePan ? "grab" : "default",
                        willChange: zoom > 1 || isPanning || imageDisplayMode === 'cover' ? "transform" : "auto",
                        height: imageDisplayMode === 'cover' ? 'auto' : undefined,
                        maxHeight: imageDisplayMode === 'cover' ? 'none' : undefined,
                      }}
                      onClick={(e) => {
                        if (e.target === e.currentTarget) {
                          setHideUI(!hideUI)
                        }
                      }}
                      onLoad={(e) => {
                        e.currentTarget.dataset.retries = "0"
                        // Debug logging for GIF loading
                        const isGif = post.fileUrl?.toLowerCase().includes('.gif') || post.sampleUrl?.toLowerCase().includes('.gif')
                        if (isGif) {
                          console.log("[v0] GIF loaded successfully:", {
                            postId: post.id,
                            src: e.currentTarget.src,
                            naturalWidth: e.currentTarget.naturalWidth,
                            naturalHeight: e.currentTarget.naturalHeight
                          })
                        }
                        // Capture natural aspect ratio for letterbox/pillarbox
                        // ads (H7) — same geometry signal the video branch
                        // reports via onLoadedMetadata.
                        const { naturalWidth: nw, naturalHeight: nh } = e.currentTarget
                        if (nw > 0 && nh > 0) setMediaNativeAR(nw / nh)
                      }}
                      onError={(e) => {
                        // A picture from the media Worker gets one retry through the Vercel proxy first (a Worker limit or outage).
                        const workerFallback = mediaWorkerFallbackUrl(e.currentTarget.src)
                        if (workerFallback) {
                          e.currentTarget.src = workerFallback
                          return
                        }
                        console.log("[v0] Image failed to load. Trying fallback URLs...")
                        const currentSrc = e.currentTarget.src
                        
                        // Helper to check if URL is an image (not video)
                        const isImageUrl = (url: string | null | undefined) => {
                          if (!url) return false
                          return !url.match(/\.(webm|mp4|mov|avi|mkv|flv|wmv|m4v|3gp|ogv)(\?|#|$)/i)
                        }
                        
                        // Try fallback chain: fileUrl -> sampleUrl (if image) -> previewUrl. An address counts as already tried when it IS the
                        // current one (direct addresses, like HentaiEra's, are not encoded inside it, which is what used to loop forever when
                        // the sample is the same file) or when it is wrapped inside the current proxy address.
                        const alreadyTried = (url: string) =>
                          isSameAddress(currentSrc, getMediaUrl(url, false)) || currentSrc.includes(encodeURIComponent(url))

                        if (post.sampleUrl && isImageUrl(post.sampleUrl) && !alreadyTried(post.sampleUrl) && takeRetry(e.currentTarget)) {
                          console.log("[v0] Trying sampleUrl:", post.sampleUrl)
                          e.currentTarget.src = getMediaUrl(post.sampleUrl, false)
                          return
                        }

                        if (post.previewUrl && !alreadyTried(post.previewUrl) && takeRetry(e.currentTarget)) {
                          console.log("[v0] Trying previewUrl:", post.previewUrl)
                          e.currentTarget.src = getMediaUrl(post.previewUrl, false)
                          return
                        }
                        
                        // All failed, show placeholder
                        console.log("[v0] All image URLs failed, showing placeholder")
                        e.currentTarget.src = "/placeholder.svg?height=400&width=400"
                      }}
                      // The media Worker only serves our own site, which it knows by the referrer (origin only, no page path).
                      referrerPolicy={isMediaWorkerUrl(getPreferredMediaUrl()) ? "origin" : "no-referrer"}
                      priority
                      draggable={false}
                    />
                    {/* Show GIF label for GIF content */}
                    {hasGifTag && (
                      <div className="absolute top-4 right-4 bg-black/70 text-white text-xs px-2 py-1 rounded font-semibold">
                        GIF
                      </div>
                    )}
                  </>
                ) : (
                  <div className="flex items-center justify-center text-white">
                    <p>{tt("noMediaAvailable")}</p>
                  </div>
                )}
    </>
  )
}
